/**
 * Capability list component.
 * Displays list of connector capabilities with enable/disable toggles.
 */

import { useState } from 'react';
import { Chip, Switch } from '@coordio/ui';
import type { ChipColor } from '@coordio/ui';
import {
  ChevronDown as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  AlertTriangle as WarningIcon,
  CircleAlert as ErrorIcon,
} from 'lucide-react';
import type { ConnectorCapabilityDto } from '../../types/connector';

export interface CapabilityListProps {
  capabilities: ConnectorCapabilityDto[];
  enabledCapabilityIds?: string[];
  onToggle?: (capabilityId: string, enabled: boolean) => void;
  showDetails?: boolean;
  readOnly?: boolean;
}

/**
 * Capability list component.
 */
export const CapabilityList = ({
  capabilities,
  enabledCapabilityIds = [],
  onToggle,
  showDetails = true,
  readOnly = false,
}: CapabilityListProps) => {
  const [expandedCapability, setExpandedCapability] = useState<string | false>(false);

  const getRiskLevelColor = (riskLevel: string): ChipColor => {
    switch (riskLevel.toLowerCase()) {
      case 'low':
        return 'green';
      case 'medium':
        return 'amber';
      case 'high':
      case 'critical':
        return 'red';
      default:
        return 'default';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low':
        return <CheckCircleIcon size={14} />;
      case 'medium':
        return <WarningIcon size={14} />;
      case 'high':
      case 'critical':
        return <ErrorIcon size={14} />;
      default:
        return undefined;
    }
  };

  const handleToggle = (capabilityId: string, currentEnabled: boolean) => {
    if (!readOnly && onToggle) {
      onToggle(capabilityId, !currentEnabled);
    }
  };

  const handleExpand = (capabilityId: string) => {
    setExpandedCapability(expandedCapability === capabilityId ? false : capabilityId);
  };

  if (capabilities.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-[var(--color-text-secondary)]">
          No capabilities available
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {capabilities.map((capability) => {
        const isEnabled = enabledCapabilityIds.includes(capability.id);
        const isExpanded = expandedCapability === capability.id;

        return (
          <div
            key={capability.id}
            className="rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] overflow-hidden"
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-[var(--color-bg-subtle)] transition-colors"
              onClick={() => showDetails && handleExpand(capability.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{capability.name}</span>
                  <Chip
                    label={capability.type}
                    variant="outlined"
                    color={capability.type === 'Action' ? 'accent' : 'violet'}
                    className="text-xs"
                  />
                  {capability.requiresApproval && (
                    <Chip
                      label="Requires Approval"
                      color="amber"
                      variant="outlined"
                      className="text-xs"
                    />
                  )}
                  <Chip
                    label={capability.riskLevel}
                    icon={getRiskLevelIcon(capability.riskLevel)}
                    color={getRiskLevelColor(capability.riskLevel)}
                    variant="outlined"
                    className="text-xs"
                  />
                </div>
                {capability.description && (
                  <p className="text-xs text-[var(--color-text-secondary)] mt-0.5 leading-snug">
                    {capability.description}
                  </p>
                )}
              </div>
              {!readOnly && (
                <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => handleToggle(capability.id, isEnabled)}
                    aria-labelledby={`capability-${capability.id}`}
                  />
                </div>
              )}
              {showDetails && (
                <ExpandMoreIcon
                  size={18}
                  className={`flex-shrink-0 text-[var(--color-text-secondary)] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </div>

            {/* Details panel */}
            {showDetails && isExpanded && (
              <div className="border-t border-[var(--color-border-default)] px-4 py-3 bg-[var(--color-bg-inset,#fafafa)]">
                <div className="grid gap-3 sm:grid-cols-2">
                  {capability.httpMethod && capability.endpointPath && (
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)] block mb-0.5">Endpoint</span>
                      <Chip label={`${capability.httpMethod} ${capability.endpointPath}`} variant="outlined" className="text-xs font-mono" />
                    </div>
                  )}
                  {capability.toolName && (
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)] block mb-0.5">Tool Name</span>
                      <span className="text-sm font-mono">{capability.toolName}</span>
                    </div>
                  )}
                  {capability.toolDescription && (
                    <div className="sm:col-span-2">
                      <span className="text-xs text-[var(--color-text-secondary)] block mb-0.5">Tool Description</span>
                      <span className="text-sm">{capability.toolDescription}</span>
                    </div>
                  )}
                  {capability.inputSchemaJson && capability.inputSchemaJson !== '{}' && (
                    <div className="sm:col-span-2">
                      <span className="text-xs text-[var(--color-text-secondary)] block mb-0.5">Input Schema</span>
                      <pre className="m-0 p-2 rounded-lg bg-[var(--color-bg-surface)] border border-[var(--color-border-default)] font-mono text-xs overflow-auto max-h-[200px] whitespace-pre-wrap break-words">
                        {JSON.stringify(JSON.parse(capability.inputSchemaJson), null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
